/**
 * Wave 3.59 — Issue for Recutting dialog.
 *
 * Opens from the Actions dropdown on a Broken raw-material packet
 * detail page. Lets the operator hand a partial (or whole) broken
 * packet's balance out to a **Vendor** or **Cutter** for physical
 * re-cutting. Uses the new `POST /api/broken-packet-issues` endpoint.
 *
 * Rules (confirmed with operator):
 *   • Recipient must be Vendor OR Cutter (never Goldsmith).
 *   • Partial issues allowed.
 *   • Multiple concurrent issues per broken packet allowed.
 *   • Wave 3.49 carat/piece validation applies (both > 0).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { formatCarats, formatINR } from '@/lib/format';
import { validateCaratMovement } from '@/lib/caratValidation';
import { extractError } from '@/lib/errors';
import { Scissors } from 'lucide-react';

const MATERIAL_META = {
  diamond:     { label: 'Diamond'     },
  color_stone: { label: 'Color Stone' },
  pearl:       { label: 'Pearl'       },
};

export default function IssueForRecutDialog({
  open, onOpenChange, material = 'diamond', packet, onIssued,
}) {
  const meta = MATERIAL_META[material] || MATERIAL_META.diamond;
  const balanceCt = Number(packet?.balance_carats || 0);
  const balancePcs = Number(packet?.balance_pieces || 0);
  const rate = Number(packet?.current_rate_per_ct || 0);

  const [form, setForm] = useState({
    recipient_type: 'cutter',
    recipient_id: '',
    carats: '',
    pieces: '',
    reference: '',
    note: '',
  });
  const [vendors, setVendors] = useState([]);
  const [cutters, setCutters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !packet) return;
    setForm({
      recipient_type: 'cutter',
      recipient_id: '',
      carats: balanceCt > 0 ? String(balanceCt) : '',
      pieces: balancePcs > 0 ? String(balancePcs) : '',
      reference: '',
      note: '',
    });
    (async () => {
      setLoading(true);
      try {
        const [v, c] = await Promise.all([
          api.get('/masters/vendors', { params: { company_id: packet.company_id } }),
          api.get('/masters/cutters', { params: { company_id: packet.company_id } }),
        ]);
        setVendors(Array.isArray(v.data) ? v.data : []);
        setCutters(Array.isArray(c.data) ? c.data : []);
      } catch {
        // best-effort
      } finally { setLoading(false); }
    })();
  }, [open, packet?.id, balanceCt, balancePcs, packet?.company_id, packet]);

  const recipientList = form.recipient_type === 'vendor' ? vendors : cutters;
  const selectedRecipient = recipientList.find((r) => r.id === form.recipient_id);

  const ct = Number(form.carats) || 0;
  const pcs = Number(form.pieces) || 0;
  const overBalance = ct - balanceCt > 1e-9 || pcs > balancePcs;
  const previewValue = useMemo(() => ct * rate, [ct, rate]);

  const setFullBalance = () => setForm((f) => ({
    ...f,
    carats: balanceCt > 0 ? String(balanceCt) : '',
    pieces: balancePcs > 0 ? String(balancePcs) : '',
  }));

  const submit = async () => {
    if (!form.recipient_id) {
      toast.error(`Select a ${form.recipient_type}`);
      return;
    }
    const err = validateCaratMovement({
      txnCarats: ct, txnPieces: pcs,
      availableCarats: balanceCt, availablePieces: balancePcs,
      packetNo: packet?.packet_no, context: 'issue for recutting',
    });
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await api.post('/broken-packet-issues', {
        material_kind: material,
        broken_packet_id: packet.id,
        recipient_type: form.recipient_type,
        recipient_id: form.recipient_id,
        recipient_name: selectedRecipient?.name,
        carats: ct,
        pieces: pcs,
        reference: form.reference || '',
        note: form.note || '',
      });
      toast.success('Broken packet issued for recutting');
      onOpenChange(false);
      if (typeof onIssued === 'function') onIssued();
    } catch (e) {
      toast.error(extractError(e, 'Issue failed'));
    } finally { setSubmitting(false); }
  };

  const disabled =
    submitting ||
    !form.recipient_id ||
    ct <= 0 || pcs <= 0 ||
    overBalance ||
    balanceCt <= 0 || balancePcs <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="issue-for-recut-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <Scissors size={18} className="text-amber-700" />
            Issue for Recutting · {packet?.packet_no}
          </DialogTitle>
          <DialogDescription>
            Hand this broken {meta.label.toLowerCase()} material to a Vendor or Cutter for re-cutting.
            When they return the material, receive it into an existing or new normal packet
            using the <b>Broken Recut Issues</b> page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-[var(--divider)] bg-slate-50 px-3 py-2 text-xs text-slate-700">
            <div className="flex items-center justify-between">
              <span className="uppercase tracking-wider text-[10px] text-slate-500">In-hand balance</span>
              <button
                type="button"
                className="text-[11px] text-[var(--ink-navy)] hover:underline"
                onClick={setFullBalance}
                data-testid="issue-recut-full-balance"
              >
                Use entire balance
              </button>
            </div>
            <div className="mt-1 font-mono tabular-nums">
              {formatCarats(balanceCt)} ct · {balancePcs} pcs · rate ₹{rate.toLocaleString('en-IN', { maximumFractionDigits: 2 })}/ct
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Recipient Type <span className="text-rose-600">*</span></Label>
              <Select
                value={form.recipient_type}
                onValueChange={(v) => setForm({ ...form, recipient_type: v, recipient_id: '' })}
              >
                <SelectTrigger data-testid="issue-recut-recipient-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cutter">Cutter</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{form.recipient_type === 'vendor' ? 'Vendor' : 'Cutter'} <span className="text-rose-600">*</span></Label>
              <Select
                value={form.recipient_id}
                onValueChange={(v) => setForm({ ...form, recipient_id: v })}
                disabled={loading || recipientList.length === 0}
              >
                <SelectTrigger data-testid="issue-recut-recipient">
                  <SelectValue placeholder={loading ? 'Loading…' : (recipientList.length === 0 ? `No ${form.recipient_type}s` : 'Select…')} />
                </SelectTrigger>
                <SelectContent>
                  {recipientList.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Carats <span className="text-rose-600">*</span></Label>
              <Input
                type="number" step="0.001" min="0" max={balanceCt}
                value={form.carats}
                onChange={(e) => setForm({ ...form, carats: e.target.value })}
                data-testid="issue-recut-carats"
              />
            </div>
            <div>
              <Label>Pieces <span className="text-rose-600">*</span></Label>
              <Input
                type="number" step="1" min="0" max={balancePcs}
                value={form.pieces}
                onChange={(e) => setForm({ ...form, pieces: e.target.value })}
                data-testid="issue-recut-pieces"
              />
            </div>
          </div>

          <div>
            <Label>Reference / Memo <span className="text-slate-400">(optional)</span></Label>
            <Input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="e.g. cutter's challan no"
              data-testid="issue-recut-reference"
            />
          </div>
          <div>
            <Label>Note <span className="text-slate-400">(optional)</span></Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Instructions, expected finish, etc."
              data-testid="issue-recut-note"
            />
          </div>

          <div className="rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2 text-xs text-slate-700" data-testid="issue-recut-preview">
            <div className="flex items-center justify-between">
              <span className="uppercase tracking-wider text-[10px] text-slate-500">Issue Preview</span>
              <span className="font-mono tabular-nums text-[var(--ink-navy)]">
                {formatCarats(ct)} ct · {pcs} pcs · {formatINR(previewValue)}
              </span>
            </div>
            {overBalance && (
              <div className="mt-2 text-[11px] text-rose-700">
                Quantity exceeds the packet's in-hand balance.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={disabled} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="issue-recut-submit">
            {submitting ? 'Issuing…' : 'Confirm Issue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
